import PropTypes from 'prop-types';
import { useState } from 'react';

import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import DndSortableItem from '#/modules/commonComponents/form/DndSortableItem';
import DndSortableList from '#/modules/commonComponents/form/DndSortableList';
import { Button } from '#/modules/commonComponents/button';

const SerialFormatterSection = ({
  errors,
  items,
  sourceOptions,
  availableSourceOptions,
  addSourceItem,
  addCustomItem,
  removeItem,
  reorderItems,
}) => {
  const [customValue, setCustomValue] = useState('');

  function handleAddCustom() {
    const added = addCustomItem(customValue);

    if (added) {
      setCustomValue('');
    }
  }

  function handleCustomKeyDown(event) {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    handleAddCustom();
  }

  const serialFormatterError = errors?.serialFormatter?.message;

  return (
    <div className="form-section @container/serial-formatter w-full space-y-4">
      <div>
        <h2 className="form-block__title">序號格式</h2>
        <p className="form-block__description">
          請從來源項目中加入格式，於右側拖曳排序。
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 @lg/serial-formatter:grid-cols-2">
        <div>
          <div>
            <h3 className="form-subsection__title form-subsection__title--spaced">加入來源</h3>

            {sourceOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                請先選擇主／次分類，並新增至少一筆規格規則。
              </p>
            ) : availableSourceOptions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {availableSourceOptions.map((source) => (
                  <Button
                    key={source.key}
                    type="button"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => addSourceItem(source)}
                  >
                    {source.chipLabel}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">所有來源項目已加入格式。</p>
            )}
            <div className="mt-4 flex flex-wrap items-top gap-2">
              <div className="min-w-0 flex-1">
                <FloatingInput
                  id="serialFormatterCustom"
                  type="text"
                  label="自訂字串"
                  value={customValue}
                  onChange={(event) => setCustomValue(event.target.value)}
                  onKeyDown={handleCustomKeyDown}
                  errors={{}}
                />
              </div>
              <Button
                className="mt-2.75"
                type="button"
                size="sm"
                onClick={handleAddCustom}
              >
                加入
              </Button>
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-col space-y-2">
          <h3 className="form-subsection__title">格式順序</h3>

          {items.length > 0 ? (
            <DndSortableList
              items={items}
              getItemId={(item) => item.id}
              onReorder={reorderItems}
              className="max-h-100 space-y-2 overflow-y-auto pr-1"
            >
              {items.map((item) => (
                <DndSortableItem key={item.id} id={item.id}>
                  <div className="flex min-h-0 justify-between items-center gap-2 overflow-hidden">
                    <span className="truncate text-sm text-gray-700">{item.label}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(item.id)}
                    >
                      移除
                    </Button>
                  </div>
                </DndSortableItem>
              ))}
            </DndSortableList>
          ) : (
            <p className="text-sm text-muted-foreground">尚未加入序號格式項目。</p>
          )}
        </div>
      </div>

      {serialFormatterError ? (
        <p className="text-sm text-red-500">{serialFormatterError}</p>
      ) : null}
    </div>
  );
};

SerialFormatterSection.propTypes = {
  errors: PropTypes.object,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sourceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      chipLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  availableSourceOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      chipLabel: PropTypes.string.isRequired,
    }),
  ).isRequired,
  addSourceItem: PropTypes.func.isRequired,
  addCustomItem: PropTypes.func.isRequired,
  removeItem: PropTypes.func.isRequired,
  reorderItems: PropTypes.func.isRequired,
};

export default SerialFormatterSection;
